import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Skeleton, Alert, Typography, Radio, Button, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { getLog, type BrevoSyncLogResponse } from '../../api/brevoSyncApi';

const { Text } = Typography;

type LogMode = 'last500' | 'full' | 'errors';

const LOG_MODES: { value: LogMode; label: string; lineas: number; soloErrores: boolean }[] = [
  { value: 'last500', label: 'Últimas 500 líneas', lineas: 500, soloErrores: false },
  { value: 'full', label: 'Log completo', lineas: 0, soloErrores: false },
  { value: 'errors', label: 'Solo errores', lineas: 0, soloErrores: true },
];

interface LogModalProps {
  visible: boolean;
  executionId: number | null;
  onClose: () => void;
}

const LogModal: React.FC<LogModalProps> = ({ visible, executionId, onClose }) => {
  const [log, setLog] = useState<BrevoSyncLogResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<LogMode>('last500');

  const fetchLog = useCallback((m: LogMode) => {
    if (executionId == null) return;
    const cfg = LOG_MODES.find(x => x.value === m)!;
    setLoading(true);
    setError(null);
    setLog(null);
    getLog(executionId, cfg.lineas, cfg.soloErrores)
      .then(data => setLog(data))
      .catch((err: any) => {
        const status = err?.response?.status;
        if (status === 502) {
          setError('El endpoint de logs de WebApiFct no está disponible todavía.');
        } else if (status === 404) {
          setError('Log no encontrado para esta ejecución.');
        } else {
          setError('Error al obtener el log.');
        }
      })
      .finally(() => setLoading(false));
  }, [executionId]);

  useEffect(() => {
    if (visible && executionId != null) {
      fetchLog(mode);
    }
  }, [visible, executionId]);

  const handleModeChange = (newMode: LogMode) => {
    setMode(newMode);
    fetchLog(newMode);
  };

  return (
    <Modal
      open={visible}
      title={`Log de ejecución ${executionId ?? ''}`}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Radio.Group
          value={mode}
          onChange={e => handleModeChange(e.target.value)}
          optionType="button"
          buttonStyle="solid"
          size="small"
          disabled={loading}
        >
          {LOG_MODES.map(m => (
            <Radio.Button key={m.value} value={m.value}>{m.label}</Radio.Button>
          ))}
        </Radio.Group>
        {log && (
          <Tooltip title="Exportar log">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => {
                const blob = new Blob([log.contenido], { type: 'text/plain;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `log_ejecucion_${executionId}_${mode}.txt`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            />
          </Tooltip>
        )}
      </div>

      {loading && <Skeleton active paragraph={{ rows: 8 }} />}

      {error && <Alert type="error" showIcon message={error} />}

      {log && (
        <>
          <pre style={{
            background: '#1e1e1e',
            color: '#d4d4d4',
            padding: 16,
            borderRadius: 8,
            fontSize: 12,
            lineHeight: 1.5,
            overflow: 'auto',
            maxHeight: 500,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}>
            {log.contenido || '(sin contenido)'}
          </pre>
          <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
            {log.lineasDevueltas.toLocaleString('es')} líneas{log.truncado ? ` de ${log.lineasTotales.toLocaleString('es')}` : ''}
          </Text>
        </>
      )}
    </Modal>
  );
};

export default LogModal;
